import dynamic from 'next/dynamic';
import { Button, Container } from 'darrius';

import { Status } from '../../../../../components';

import { OrderBackArrow } from '../OrderDetailsSections';
import { OrderCategoryTitle } from '../../OrderCategoryTitle/OrderCategoryTitle';

import styles from './OrderDetails.module.scss';

import { type TOrderDetailPageProps } from '../../../../../modules/orderDetails/pageProps/@interfaces';
import { type TServiceExpressOffer } from '../../../../../modules/serviceExpress';
import { type TPro } from '../../../../../modules/instantMatch/transformations';

const OrderStatusAlert = dynamic(
  () => import('../OrderDetailsSections').then((mod) => mod.OrderStatusAlert),
  {
    ssr: false,
  }
);

const OrderStatusMessage = dynamic(
  () =>
    import('../OrderDetailsSections/').then((mod) => mod.OrderStatusMessage),
  {
    ssr: false,
  }
);

const ServiceExpressCard = dynamic(
  () =>
    import('../OrderDetailsSections/').then((mod) => mod.ServiceExpressCard),
  {
    ssr: false,
  }
);

const ExpressProsList = dynamic(
  () => import('../OrderDetailsSections/').then((mod) => mod.ExpressProsList),
  {
    ssr: false,
  }
);

const FoundProfessionalsList = dynamic(
  () =>
    import('../OrderDetailsSections/').then(
      (mod) => mod.FoundProfessionalsList
    ),
  {
    ssr: false,
  }
);

interface IOrderDetailsProps {
  onShowReviewOrder: () => void;
  order: TOrderDetailPageProps;
  serviceExpress: TServiceExpressOffer;
  expressPros: TPro[];
  isExpressPurchased: boolean | null;
}

export function OrderDetails({
  onShowReviewOrder,
  order,
  serviceExpress,
  expressPros = [],
  isExpressPurchased,
}: IOrderDetailsProps) {
  const { status, category, categoryRoot, uuid, leads } = order;

  /**
   * A oferta some assim que a compra é confirmada. Com o express-check
   * indisponível (`null`) a oferta continua visível: melhor oferecer de novo
   * do que sumir com a compra por instabilidade de um serviço externo.
   */
  const shouldShowServiceExpressOffer =
    serviceExpress?.isServiceExpress && isExpressPurchased !== true;

  /**
   * A lista fica escondida só quando o express-check afirma que não houve
   * compra — com ele indisponível, os profissionais que já vieram do
   * `cli_store` continuam acessíveis.
   */
  const shouldShowExpressPros =
    !!expressPros.length && isExpressPurchased !== false;

  function renderDetails() {
    if (status === 'blocked' || status === 'finalized_zero_lead') {
      return <OrderStatusAlert status={status} />;
    }

    if (
      (status === 'searching' && leads.length === 0) ||
      status === 'waiting'
    ) {
      return <OrderStatusMessage status={status} />;
    }

    return (
      <FoundProfessionalsList
        leads={order.leads}
        reviewUrl={order.reviewUrl}
        uuid={uuid}
      />
    );
  }

  return (
    <main>
      <div className={styles.container}>
        <Container className={styles.container_content}>
          <OrderBackArrow to="/meus-pedidos" />

          <div className={styles.container_content__category}>
            <OrderCategoryTitle
              category={{
                text: category,
                as: 'h1',
              }}
              rootCategory={{
                text: categoryRoot,
                as: 'h2',
              }}
            />

            <Button.Root variation="clear" onClick={onShowReviewOrder}>
              Rever pedido
            </Button.Root>
          </div>

          <Status.Root>
            <Status.Title
              status={order.hasNotifications ? 'notification' : order.status}
              as="h3"
              remainingHours={order.remainingHours}
            />
            <Status.Description
              status={order.hasNotifications ? 'notification' : order.status}
              as="h4"
              totalProfessionalsFound={order.totalProfessionalsFound}
            />
          </Status.Root>
        </Container>
      </div>

      <div className={styles.content}>
        {shouldShowServiceExpressOffer && (
          <ServiceExpressCard
            uuid={uuid}
            id={order.id}
            price={serviceExpress.serviceExpressPrice}
          />
        )}

        {shouldShowExpressPros && <ExpressProsList pros={expressPros} />}

        {renderDetails()}
      </div>
    </main>
  );
}
